import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import alertService from "../../utils/alertService";
import apiClient from "../../services/apiClient";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import vendorService from "../../services/vendorService";
import purchaseMasterService from "../../services/purchaseMasterService";
import Modal from "../../components/common/Modal";
import { formatCurrency } from "../../utils/currency";
import { formatDate, formatDateTime } from "../../utils/dateUtils";

const BL_STAGES = [
  "SI Pending",
  "Loading",
  "In Transit",
  "Arrived",
  "Arrived ICD",
];

function BLListPage() {
  const navigate = useNavigate();
  const basePath = "/bl-master";

  const [vendorOptions, setVendorOptions] = useState([]);
  const [purchaseOptions, setPurchaseOptions] = useState([]);
  const [selectedBLForActivity, setSelectedBLForActivity] = useState(null);
  const [loadingBLId, setLoadingBLId] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadOptions = async () => {
      try {
        const [vRes, pRes] = await Promise.all([
          vendorService.getActiveVendors(),
          purchaseMasterService.getActivePurchaseMasters(),
        ]);
        if (isMounted) {
          const vItems = vRes?.items || vRes?.data || vRes || [];
          setVendorOptions(
            vItems.map((v) => ({
              label: v.vendorName || v.name,
              value: v.vendorId || v.id,
            })),
          );

          const pItems = pRes?.items || pRes?.data || pRes || [];
          setPurchaseOptions(
            pItems.map((p) => ({
              label: p.billNo,
              value: p.purchaseMasterId || p.id,
            })),
          );
        }
      } catch (err) {
        console.error("Failed to load options", err);
      }
    };
    loadOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  const endpoints = useMemo(
    () => ({
      list: "/bl-master",
      create: "/bl-master",
      edit: (id) => `/bl-master/${id}`,
      update: (id) => `/bl-master/${id}`,
      delete: (id) => `/bl-master/${id}`,
    }),
    [],
  );

  const {
    rows,
    isLoading,
    error,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
  } = useCrud({
    endpoints,
    idKey: "blId",
    updateMethod: "patch",
    initialFilters: { search: "", vendorId: "", status: "" },
    initialSort: { key: "createdDate", order: "DESC" },
  });

  const tableColumns = useMemo(
    () => [
      {
        key: "billNo",
        label: "Bill No",
        sortable: false,
        render: (item) => item.billNo || item.purchase?.billNo || "-",
      },
      {
        key: "blNumber",
        label: "BL Number",
        sortable: true,
        className: "font-bold text-slate-800 dark:text-white",
      },
      {
        key: "bookingNo",
        label: "Booking No",
        sortable: true,
        render: (item) => item?.bookingNo || "-",
      },
      {
        key: "netWeight",
        label: "Net Weight (KG)",
        sortable: false,
        className: "text-right font-semibold text-slate-700 dark:text-slate-300",
        headerClassName: "text-right",
        render: (item) => {
          // ponytail: fallback to stockDetail or item direct netWeight if purchase.netWeight is missing
          const val = item.purchase?.netWeight ?? item.stockDetail?.netWeight ?? item.netWeight;
          return val ? Number(val).toLocaleString() : "-";
        },
      },
      {
        key: "vendorName",
        label: "Vendor",
        sortable: false,
        className: "text-slate-600 dark:text-slate-400",
      },
      {
        key: "shippingLineName",
        label: "Shipping Line",
        sortable: false,
      },
      {
        key: "dateofDeparture",
        label: "Departure",
        sortable: true,
        render: (item) => formatDate(item?.dateofDeparture),
      },
      {
        key: "eta",
        label: "ETA",
        sortable: true,
        render: (item) => formatDate(item?.eta),
      },
      {
        key: "etaIcd",
        label: "ETA ICD",
        sortable: true,
        render: (item) => formatDate(item?.etaIcd),
      },
      {
        key: "remainingDays",
        label: "Remaining Days",
        // sortable: true,
        className: "text-right font-bold ",
        headerClassName: "text-right",
        render: (item) => item?.remainingDays || "0",
      },
      {
        key: "daysLeft",
        label: "Free Days Left",
        sortable: false,
        className: "text-right font-black",
        headerClassName: "text-right",
        render: (item) => {
          if (!item?.etaIcd) return "-";
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const target = new Date(item.etaIcd);
          target.setHours(0, 0, 0, 0);
          const diffTime = target - today;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays < 0) {
            // ponytail: checks if status is Arrived/Arrived ICD using case-insensitive check
            const isArrived = item?.status?.toLowerCase().includes("arrived");
            if (isArrived) {
              return <span className="text-slate-600">0</span>;
            }
            return (
              <span className="text-rose-600">
                Overdue ({Math.abs(diffDays)})
              </span>
            );
          }
          if (diffDays === 0)
            return <span className="text-amber-600 animate-pulse">TODAY</span>;
          return (
            <span
              className={diffDays <= 3 ? "text-amber-500" : "text-slate-600"}
            >
              {diffDays} Days
            </span>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        render: (item) => {
          const status = item?.status || "SI Pending";
          const colors = {
            "SI Pending":
              "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
            Loading:
              "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
            "In Transit":
              "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
            Arrived:
              "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
            "Arrived ICD":
              "bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-400",
          };
          return (
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  setLoadingBLId(item.blId);
                  try {
                    const res = await apiClient.get(`/bl-master/${item.blId}`);
                    const detail = res?.data?.data || res?.data || {};
                    setSelectedBLForActivity(detail);
                  } catch (err) {
                    console.error("Failed to fetch BL details", err);
                    setSelectedBLForActivity(item);
                  } finally {
                    setLoadingBLId(null);
                  }
                }}
                disabled={loadingBLId !== null}
                className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider hover:scale-105 transition-transform ${colors[status] || colors["SI Pending"]} ${loadingBLId === item.blId ? "opacity-75 cursor-wait" : ""}`}
              >
                {loadingBLId === item.blId ? (
                  <svg
                    className="animate-spin -ml-0.5 mr-1 h-3 w-3 text-current"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : null}
                {status}
              </button>
              {item?.isConverted && (
                <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-blue-700 dark:bg-blue-500/20 dark:text-blue-400">
                  Converted
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: "totalExpense",
        label: "Total Expense",
        // sortable: true,
        className: "text-right font-black text-rose-600",
        headerClassName: "text-right",
        render: (item) => formatCurrency(item?.totalExpense || 0, "INR"),
      },
      {
        key: "blFileUpload",
        label: "File",
        render: (item) => {
          const url = item?.blFileUploadUrl || item?.blFileUpload;
          if (!url) return <span className="text-slate-300">-</span>;
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 font-bold flex items-center gap-1"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              PDF
            </a>
          );
        },
      },
    ],
    [purchaseOptions],
  );

  const openEdit = (item) => {
    if (item?.isConverted) {
      alertService.warning(
        "All the stock items of this purchase are converted.",
      );
      return;
    }
    navigate(`${basePath}/${encrypt(item?.blId)}/edit`);
  };

  const selectedStatus = selectedBLForActivity?.status || "SI Pending";
  const selectedStageIndex = Math.max(BL_STAGES.indexOf(selectedStatus), 0);
  const progressPercent = Math.round(
    ((selectedStageIndex + 1) / BL_STAGES.length) * 100,
  );
  const lastUpdated =
    selectedBLForActivity?.modifiedDate || selectedBLForActivity?.createdDate;

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={(item) => {
          if (item?.isConverted) {
            alertService.warning("Converted records cannot be deleted.");
            return;
          }
          askDelete(item);
        }}
        renderActions={(item) => (
          <Button
            variant="ghost"
            size="sm"
            className="px-2 py-1 flex items-center border justify-center gap-1.5 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            onClick={() =>
              navigate(`${basePath}/${encrypt(item?.blId)}/expenses`)
            }
            title="Manage Expenses"
          >
            + Add Expense
          </Button>
        )}
        searchKey="search"
        searchPlaceholder="Search by BL No or Booking No..."
        tableScrollHeight="58vh"
        tableClassName="min-w-[1500px]"
        toolbarFilters={
          <div className="flex gap-2 min-w-[300px]">
            <SearchableDropdown
              placeholder="Filter by Vendor"
              options={[{ value: "", label: "All Vendors" }, ...vendorOptions]}
              value={filters.vendorId || ""}
              onChange={(e) =>
                updateFilters({ vendorId: e.target.value, page: 1 })
              }
            />
          </div>
        }
        toolbarAction={
          <Button
            onClick={() => navigate("/expense-overhead/new")}
            className="flex items-center gap-2"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Add Overhead Expense
          </Button>
        }
      />

      <Modal
        isOpen={Boolean(selectedBLForActivity)}
        onClose={() => setSelectedBLForActivity(null)}
        title="BL Shipment Status"
      >
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold">
                  BL Number
                </p>
                <p className="text-sm font-black">
                  {selectedBLForActivity?.blNumber}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold">
                  Current Stage
                </p>
                <p className="text-sm font-black text-emerald-600 dark:text-emerald-400">
                  {selectedStatus}
                </p>
              </div>
            </div>
          </div>

          <div className="px-1">
            <div className="flex justify-between items-end mb-2">
              <span className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-tight">
                Shipping Progress
              </span>
              <span className="text-xs font-black text-emerald-600 dark:text-emerald-400">
                {progressPercent}%
              </span>
            </div>
            <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="space-y-2 max-h-[50vh] pr-1 overflow-auto">
            {BL_STAGES.map((stage, index) => {
              const isDone = index < selectedStageIndex;
              const isActive = index === selectedStageIndex;
              const isDoneOrActive = index <= selectedStageIndex;
              return (
                <div
                  key={stage}
                  className={`flex gap-3 p-3 rounded-xl border transition-all ${isActive ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800" : "bg-white border-slate-100 dark:bg-slate-900 dark:border-slate-800"}`}
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={`h-6 w-6 rounded-full flex items-center justify-center border-2 ${isDoneOrActive ? "bg-emerald-500 border-emerald-500" : "border-slate-200"}`}
                    >
                      {isDoneOrActive ? (
                        <svg
                          className="h-4 w-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <div
                          className="h-1.5 w-1.5 rounded-full bg-slate-200"
                        />
                      )}
                    </div>
                    {index < BL_STAGES.length - 1 && (
                      <div
                        className={`w-0.5 h-full min-h-[10px] mt-1 ${isDone ? "bg-emerald-500" : "bg-slate-100 dark:bg-slate-800"}`}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-xs font-black uppercase tracking-tight ${isActive ? "text-emerald-700 dark:text-emerald-400" : isDone ? "text-emerald-700 dark:text-emerald-400" : "text-slate-400"}`}
                    >
                      {stage}
                    </p>
                    {isActive && (
                      <p className="text-[10px] text-emerald-600/70 font-semibold italic mt-0.5">
                        Current Status
                      </p>
                    )}
                    {(isDone || isActive) && (
                      <p className="text-[9px] text-slate-500 mt-1">
                        Updated {formatDateTime(lastUpdated)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* <Button variant="secondary" className="w-full" onClick={() => setSelectedBLForActivity(null)}>Close Activity View</Button> */}
        </div>
      </Modal>
    </div>
  );
}

export default BLListPage;
